<template>
  <v-btn
      :loading="exporting"
      :disabled="disabled || exporting"
      color="success"
      elevation="2"
      @click="exportarExcel"
      v-bind="$attrs"
  >
    <v-icon class="mr-2">mdi-microsoft-excel</v-icon>
    <span>{{ label }}</span>
  </v-btn>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ExcelExporter',

  props: {
    dados: {
      type: Array,
      required: true,
      default: () => []
    },
    filename: {
      type: String,
      default: 'exportacao'
    },
    label: {
      type: String,
      default: 'Exportar Excel'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    bonificacoes: {
      type: Array,
      default: () => []
    },
    tipo: {
      type: String,
      default: 'vendedores',
      validator: (value) => ['vendedores', 'convenios', 'motoristas', 'completo'].includes(value)
    },
    // Array de { key, label } com as colunas visíveis — passado pelo ExportadorTabela.
    colunasVisiveis: {
      type: Array,
      default: () => []
    },
    mostrarComissaoBase: {
      type: Boolean,
      default: true
    },
    // Necessários apenas quando tipo === 'completo'
    convenios: {
      type: Array,
      default: () => []
    },
    motoristas: {
      type: Array,
      default: () => []
    },
  },

  data: () => ({
    exporting: false
  }),

  computed: {
    // Filtra vendedores do array `dados`
    dadosVendedores() {
      return this.dados.filter(d =>
          d.meta !== undefined || d.vendabase !== undefined ||
          d.comissaobase !== undefined || (d.bonificacoes && Object.keys(d.bonificacoes).length > 0)
      );
    },
    // Filtra motoristas do array `dados`
    dadosMotoristas() {
      if (this.tipo === 'completo') return this.motoristas;
      return this.dados.filter(d => d.meta_entregas !== undefined);
    },
    // Fonte de convênios dependendo do modo
    dadosConvenios() {
      if (this.tipo === 'completo') return this.convenios;
      return this.dados;
    },
  },

  methods: {
    async exportarExcel() {
      const temDados = this.dados.length > 0 || this.convenios.length > 0 || this.motoristas.length > 0;
      if (!temDados) {
        this.$emit('error', 'Não há dados para exportar');
        return;
      }

      this.exporting = true;

      try {
        const wb = XLSX.utils.book_new();

        if (this.tipo === 'completo') {
          this.montarWorkbookCompleto(wb);
        } else {
          const dadosAoa = this.prepararDadosPorTipo();
          const ws       = XLSX.utils.aoa_to_sheet(dadosAoa);
          this.aplicarEstilosPorTipo(ws);
          XLSX.utils.book_append_sheet(wb, ws, this.obterNomeAba());
        }

        const timestamp   = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
        const nomeArquivo = `${this.filename}_${timestamp}.xlsx`;
        XLSX.writeFile(wb, nomeArquivo);

        this.$emit('success', 'Arquivo Excel exportado com sucesso!');
      } catch (error) {
        console.error('Erro ao exportar Excel:', error);
        this.$emit('error', 'Erro ao exportar arquivo Excel');
      } finally {
        this.exporting = false;
      }
    },

    // ── Workbook completo (múltiplas abas) ───────────────────────────────────
    montarWorkbookCompleto(wb) {
      // Aba Vendedores — só se houver dados e comissão base selecionada
      if (this.dadosVendedores.length > 0) {
        const wsVendedores = XLSX.utils.aoa_to_sheet(this.prepararDadosVendedores(this.dadosVendedores));
        this.aplicarEstilosVendedores(wsVendedores, this.dadosVendedores);
        XLSX.utils.book_append_sheet(wb, wsVendedores, 'Vendedores');
      }

      // Aba Convênios
      if (this.dadosConvenios.length > 0) {
        const wsConvenios = XLSX.utils.aoa_to_sheet(this.prepararDadosConvenios(this.dadosConvenios));
        this.aplicarEstilosConvenios(wsConvenios, this.dadosConvenios);
        XLSX.utils.book_append_sheet(wb, wsConvenios, 'Convênios');
      }

      // Aba Motoristas
      if (this.dadosMotoristas.length > 0) {
        const wsMotoristas = XLSX.utils.aoa_to_sheet(this.prepararDadosMotoristas(this.dadosMotoristas));
        this.aplicarEstilosMotoristas(wsMotoristas, this.dadosMotoristas);
        XLSX.utils.book_append_sheet(wb, wsMotoristas, 'Motoristas');
      }
    },

    obterNomeAba() {
      return {
        vendedores: 'Vendedores',
        convenios:  'Convênios',
        motoristas: 'Motoristas',
        completo:   'Dados',
      }[this.tipo] || 'Dados';
    },

    prepararDadosPorTipo() {
      switch (this.tipo) {
        case 'vendedores': return this.prepararDadosVendedores(this.dados);
        case 'convenios':  return this.prepararDadosConvenios(this.dados);
        case 'motoristas': return this.prepararDadosMotoristas(this.dados);
        default:           return this.prepararDadosVendedores(this.dados);
      }
    },

    // ── Vendedores ───────────────────────────────────────────────────────────
    // A planilha exporta SEMPRE todas as colunas, independente do que está
    // visível na tela. colunasVisiveis controla apenas a exibição no frontend.
    // DEPOIS
    prepararDadosVendedores(fonte) {
      const COLUNAS_COMPLETAS = [
        { key: 'meta',                  label: 'Meta',                    percentual: false },
        { key: 'vendabase',             label: 'Venda Base',              percentual: false },
        // { key: 'devolucao',        label: 'Devolucao',      percentual: false },
        { key: 'permeta',               label: '% Meta',                  percentual: true  },
        { key: 'margem',                label: '% Margem',                percentual: true  },
        { key: 'percomissao',           label: '% Rendimento Var.',       percentual: true  },
        { key: 'comissaobase',          label: 'Rendimento Var.',         percentual: false },
        { key: 'salariobase',           label: 'Salario Base',            percentual: false },
        { key: 'salario_proporcional',  label: 'Salario Proporcional',    percentual: false },
        { key: 'premiacao',             label: 'Premiacao',               percentual: false },
      ];

      const cabecalho = ['Filial', 'Cadastro', 'Vendedor'];   // ← adicionou CAD
      COLUNAS_COMPLETAS.forEach(c => cabecalho.push(c.label));
      this.bonificacoes.forEach(bonif => {
        cabecalho.push(`Venda ${bonif}`);
        cabecalho.push(`Comissao ${bonif}`);
      });

      const dados = [cabecalho];

      fonte.forEach(v => {
        const linha = [v.filial, v.codsenior, v.vendedor];   // ← filial + codsenior + vendedor
        COLUNAS_COMPLETAS.forEach(c => linha.push(v[c.key] ?? 0));
        this.bonificacoes.forEach(bonif => {
          linha.push(v.bonificacoes?.[bonif]?.vendatotal || 0);
          linha.push(v.bonificacoes?.[bonif]?.comissao   || 0);
        });
        dados.push(linha);
      });

      // Linha de totais
      const totais = ['TOTAL GERAL', '', ''];   // ← 3 células fixas agora
      COLUNAS_COMPLETAS.forEach(c => {
        totais.push(c.percentual
            ? this.mediaCampoFonte(fonte, c.key)
            : this.somarCampoFonte(fonte, c.key)
        );
      });
      this.bonificacoes.forEach(bonif => {
        totais.push(this.somarBonificacaoVendaFonte(fonte, bonif));
        totais.push(this.somarBonificacaoComissaoFonte(fonte, bonif));
      });
      dados.push(totais);

      return dados;
    },

    // ── Convênios ────────────────────────────────────────────────────────────
    // Usa o modelo ConvenioValores:
    //   idfilial, colaborador, cpfcolaborador, cnpjempresa,
    //   qtdtitulos, totaldescontos, titulos[]
    prepararDadosConvenios(fonte) {
      const cabecalho = ['Filial', 'Colaborador', 'CPF', 'Qtd. Títulos', 'Total a Descontar'];
      const dados = [cabecalho];

      fonte.forEach(c => {
        dados.push([
          c.idfilial,
          c.colaborador     || '',
          this.formatarCpf(c.cpfcolaborador),
          c.qtdtitulos      || 0,
          c.totaldescontos  || 0,
        ]);
      });

      dados.push([
        'TOTAL GERAL', '', '',
        this.somarCampoFonte(fonte, 'qtdtitulos'),
        this.somarCampoFonte(fonte, 'totaldescontos'),
      ]);

      return dados;
    },

    // ── Motoristas ───────────────────────────────────────────────────────────
    prepararDadosMotoristas(fonte) {
      const dados = [['Filial', 'Motorista', 'Meta Entregas', 'Qtd Entregas', 'Valor/Entrega', 'Bonificação', 'Status Meta']];

      fonte.forEach(m => {
        const meta = m.meta_entregas || 0;
        const qtd  = m.qtd_entregas  || 0;
        dados.push([
          m.idfilial,
          m.vendedor,
          meta,
          qtd,
          m.valor_por_entrega || 0,
          m.valor_bonificacao || 0,
          qtd >= meta ? 'ATINGIU' : 'NÃO ATINGIU',
        ]);
      });

      dados.push([
        'TOTAL GERAL', '',
        this.somarCampoFonte(fonte, 'meta_entregas'),
        this.somarCampoFonte(fonte, 'qtd_entregas'),
        '-',
        this.somarCampoFonte(fonte, 'valor_bonificacao'),
        '',
      ]);

      return dados;
    },

    // ── Estilos ──────────────────────────────────────────────────────────────
    aplicarEstilosPorTipo(ws) {
      switch (this.tipo) {
        case 'vendedores': this.aplicarEstilosVendedores(ws, this.dados); break;
        case 'convenios':  this.aplicarEstilosConvenios(ws, this.dados);  break;
        case 'motoristas': this.aplicarEstilosMotoristas(ws, this.dados); break;
      }
    },

    // DEPOIS — 9 colunas fixas, colInicioNum = 3 (Filial + CAD + Vendedor)
    aplicarEstilosVendedores(ws, fonte) {
      const COLUNAS_COMPLETAS = [
        { percentual: false }, // meta
        { percentual: false }, // vendabase
        // { percentual: false }, // devolucao
        { percentual: true  }, // permeta
        { percentual: true  }, // margem
        { percentual: true  }, // percomissao
        { percentual: false }, // comissaobase
        { percentual: false }, // salariobase
        { percentual: false }, // salario_proporcional
        { percentual: false }, // premiacao
      ];

      // Filial + CAD + Vendedor + 9 colunas fixas + bonificações (2 cols cada)
      const colsWidths = [{ wch: 16 }, { wch: 10 }, { wch: 30 }];   // ← adicionou CAD
      COLUNAS_COMPLETAS.forEach(c => colsWidths.push({ wch: c.percentual ? 12 : 18 }));
      this.bonificacoes.forEach(() => {
        colsWidths.push({ wch: 16 });
        colsWidths.push({ wch: 16 });
      });

      ws['!cols']   = colsWidths;
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      const range        = XLSX.utils.decode_range(ws['!ref']);
      const colInicioNum = 3;   // ← era 2, agora é 3
      const totalRows    = fonte.length + 1;

      for (let R = 1; R <= range.e.r; R++) {
        const ehTotais = R === totalRows;

        for (let C = colInicioNum; C <= range.e.c; C++) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[addr]) continue;

          const idxColuna = C - colInicioNum;
          const col       = COLUNAS_COMPLETAS[idxColuna];

          if (col && col.percentual) {
            ws[addr].z = '0.00"%"';
          } else {
            ws[addr].z = 'R$ #,##0.00';
          }

          if (ehTotais) {
            ws[addr].s = { font: { bold: true } };
          }
        }
      }
    },

    aplicarEstilosConvenios(ws, fonte) {
      ws['!cols'] = [
        { wch: 10 }, // Filial
        { wch: 32 }, // Colaborador
        { wch: 16 }, // CPF
        { wch: 14 }, // Qtd. Títulos
        { wch: 18 }, // Total a Descontar
      ];
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      const range     = XLSX.utils.decode_range(ws['!ref']);
      const totalRows = fonte.length + 1;

      for (let R = 1; R <= range.e.r; R++) {
        const ehTotais = R === totalRows;

        // Qtd. Títulos — coluna 3
        const cellQtd = XLSX.utils.encode_cell({ r: R, c: 3 });
        if (ws[cellQtd]) {
          ws[cellQtd].z = '#,##0';
          if (ehTotais) ws[cellQtd].s = { font: { bold: true } };
        }

        // Total a Descontar — coluna 4
        const cellTotal = XLSX.utils.encode_cell({ r: R, c: 4 });
        if (ws[cellTotal]) {
          ws[cellTotal].z = 'R$ #,##0.00';
          if (ehTotais) ws[cellTotal].s = { font: { bold: true } };
        }
      }
    },

    aplicarEstilosMotoristas(ws, fonte) {
      ws['!cols'] = [
        { wch: 10 }, // Filial
        { wch: 30 }, // Motorista
        { wch: 15 }, // Meta Entregas
        { wch: 15 }, // Qtd Entregas
        { wch: 15 }, // Valor/Entrega
        { wch: 15 }, // Bonificação
        { wch: 14 }, // Status Meta
      ];
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      const range     = XLSX.utils.decode_range(ws['!ref']);
      const totalRows = fonte.length + 1;

      for (let R = 1; R <= range.e.r; R++) {
        const ehTotais = R === totalRows;

        const cellMeta = XLSX.utils.encode_cell({ r: R, c: 2 });
        if (ws[cellMeta]) {
          ws[cellMeta].z = '#,##0';
          if (ehTotais) ws[cellMeta].s = { font: { bold: true } };
        }

        const cellQtd = XLSX.utils.encode_cell({ r: R, c: 3 });
        if (ws[cellQtd]) {
          ws[cellQtd].z = '#,##0';
          if (ehTotais) ws[cellQtd].s = { font: { bold: true } };
        }

        const cellValor = XLSX.utils.encode_cell({ r: R, c: 4 });
        if (ws[cellValor] && ws[cellValor].v !== '-') {
          ws[cellValor].z = 'R$ #,##0.00';
        }

        const cellBonif = XLSX.utils.encode_cell({ r: R, c: 5 });
        if (ws[cellBonif]) {
          ws[cellBonif].z = 'R$ #,##0.00';
          if (ehTotais) ws[cellBonif].s = { font: { bold: true } };
        }

        // Status Meta — apenas linhas de dados (não totais)
        if (!ehTotais) {
          const cellStatus = XLSX.utils.encode_cell({ r: R, c: 6 });
          if (ws[cellStatus]) {
            const atingiu = ws[cellStatus].v === 'ATINGIU';
            ws[cellStatus].s = {
              alignment: { horizontal: 'center' },
              fill: { fgColor: { rgb: atingiu ? '22c55e' : 'f97316' } },
              font: { color: { rgb: 'FFFFFF' }, bold: true },
            };
          }
        }
      }
    },

    // ── Utilitários ──────────────────────────────────────────────────────────
    // Versões que recebem a fonte explicitamente (necessário para tipo completo)
    somarCampoFonte(fonte, campo) {
      return fonte
          .filter(v => v[campo] !== undefined)
          .reduce((a, v) => a + (v[campo] || 0), 0);
    },

    mediaCampoFonte(fonte, campo) {
      const com = fonte.filter(v => v[campo] !== undefined && v[campo] !== 0);
      if (!com.length) return 0;
      return com.reduce((a, v) => a + v[campo], 0) / com.length;
    },

    somarBonificacaoVendaFonte(fonte, codigo) {
      return fonte.reduce((t, v) => t + (v.bonificacoes?.[codigo]?.vendatotal || 0), 0);
    },

    somarBonificacaoComissaoFonte(fonte, codigo) {
      return fonte.reduce((t, v) => t + (v.bonificacoes?.[codigo]?.comissao || 0), 0);
    },

    // Mantidos por compatibilidade (usados quando tipo !== 'completo')
    somarCampo(campo)     { return this.somarCampoFonte(this.dados, campo); },
    mediaCampo(campo)     { return this.mediaCampoFonte(this.dados, campo); },

    formatarCpf(cpf) {
      if (!cpf) return '';
      const s = String(cpf).replace(/\D/g, '').padStart(11, '0');
      return `${s.slice(0,3)}.${s.slice(3,6)}.${s.slice(6,9)}-${s.slice(9,11)}`;
    },
  },
};
</script>

<style scoped></style>